import React, { useState, useMemo, useEffect } from 'react';
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Clock,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  MessageSquare,
  CheckCircle,
  Sparkles,
  ArrowRight,
  Headphones,
  Calendar,
  Globe,
  Shield,
  Zap
} from 'lucide-react';
import { COMPANY_INFO } from '../utils/constants';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Animation state for floating elements
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) errors.message = 'Message is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Memoized contact methods with enhanced styling
  const contactMethods = useMemo(() => [
    {
      icon: <Phone className="w-8 h-8 md:w-10 md:h-10" />,
      title: 'Phone Support',
      value: COMPANY_INFO.contact.phone,
      link: `tel:${COMPANY_INFO.contact.phone}`,
      gradient: 'from-blue-500 to-cyan-500',
      color: 'text-blue-600 dark:text-blue-400',
      description: '24/7 dedicated support line',
      delay: '0s'
    },
    {
      icon: <Mail className="w-8 h-8 md:w-10 md:h-10" />,
      title: 'Email Us',
      value: COMPANY_INFO.contact.email,
      link: `mailto:${COMPANY_INFO.contact.email}`,
      gradient: 'from-green-500 to-emerald-500',
      color: 'text-green-600 dark:text-green-400',
      description: 'Response within 1 business hour',
      delay: '0.1s'
    },
    {
      icon: <MapPin className="w-8 h-8 md:w-10 md:h-10" />,
      title: 'Visit Office',
      value: COMPANY_INFO.contact.address,
      link: 'https://www.google.com/maps/search/?api=1&query=13.331752682179276,77.09751420210712',
      gradient: 'from-purple-500 to-pink-500',
      color: 'text-purple-600 dark:text-purple-400',
      description: 'Schedule a meeting in advance',
      delay: '0.2s'
    },
    {
      icon: <Headphones className="w-8 h-8 md:w-10 md:h-10" />,
      title: 'Live Chat',
      value: 'Available 9AM-6PM IST',
      gradient: 'from-orange-500 to-amber-500',
      color: 'text-orange-600 dark:text-orange-400',
      description: 'Instant messaging support',
      delay: '0.3s'
    },
  ], []);

  // Memoized stats with animations
  const stats = useMemo(() => [
    { value: '24/7', label: 'Support Available', icon: <Shield className="w-6 h-6" />, delay: '0s' },
    { value: '<1hr', label: 'Response Time', icon: <Zap className="w-6 h-6" />, delay: '0.1s' },
    { value: '250+', label: 'Happy Clients', icon: <CheckCircle className="w-6 h-6" />, delay: '0.2s' },
    { value: '50+', label: 'Projects Delivered', icon: <Globe className="w-6 h-6" />, delay: '0.3s' },
  ], []);

  // Memoized social links with enhanced styling
  const socialLinks = useMemo(() => [
    { 
      icon: <Facebook className="w-5 h-5 md:w-6 md:h-6" />, 
      link: 'https://www.facebook.com/nevxoaulio', 
      label: 'Facebook',
      gradient: 'from-blue-600 to-blue-800'
    },
    { 
      icon: <Twitter className="w-5 h-5 md:w-6 md:h-6" />, 
      link: 'https://x.com/nevxoaulio', 
      label: 'Twitter',
      gradient: 'from-sky-500 to-blue-500'
    },
    { 
      icon: <Linkedin className="w-5 h-5 md:w-6 md:h-6" />, 
      link: 'https://www.linkedin.com/in/nevxoaulio/', 
      label: 'LinkedIn',
      gradient: 'from-blue-700 to-blue-900'
    },
    { 
      icon: <Instagram className="w-5 h-5 md:w-6 md:h-6" />, 
      link: 'https://www.instagram.com/nevxoaulio/', 
      label: 'Instagram',
      gradient: 'from-pink-600 to-rose-600'
    },
  ], []);

  const handleSubmit = async e => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setSubmitted(false);

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        alert(data.error || 'Failed to send message');
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
      setFormErrors({});

      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error('Contact form error:', err);
      alert('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 overflow-hidden">
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-64 h-64 md:w-96 md:h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 md:w-[32rem] md:h-[32rem] bg-purple-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className={`transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
                <Sparkles className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Let's Build Something Amazing</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight">
                <span className="block bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                  Get In Touch
                </span>
                <span className="block text-3xl sm:text-4xl md:text-5xl mt-4 text-blue-200">
                  With Our Team
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed max-w-2xl">
                Have a project in mind? Let's discuss how we can help you achieve your goals. 
                We're here to turn your ideas into reality with cutting-edge solutions.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`tel:${COMPANY_INFO.contact.phone}`}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-50 hover:scale-105 active:scale-95 transition-all duration-300 shadow-xl hover:shadow-2xl"
                >
                  <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span>Call Now</span>
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all" />
                </a>
                <a
                  href={`mailto:${COMPANY_INFO.contact.email}`}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-transparent border-2 border-white rounded-xl font-semibold hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-300 shadow-xl hover:shadow-2xl"
                >
                  <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>Email Us</span>
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all" />
                </a>
              </div>
            </div>
            
            <div className={`hidden lg:block transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop&q=80"
                  alt="Contact us"
                  className="rounded-3xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center text-white text-4xl shadow-2xl animate-float">
                  📱
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`group bg-white dark:bg-gray-900 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-800 animate-fade-in-up hover:-translate-y-2`}
                style={{ animationDelay: stat.delay }}
              >
                <div className="flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4 transform group-hover:scale-110 transition-transform">
                  {stat.icon}
                </div>
                <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-2 text-center font-mono">
                  {stat.value}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-center text-sm sm:text-base md:text-lg font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
              <MessageSquare className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Multiple Ways to Connect</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Contact <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Methods</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Reach out through any channel that works best for you. Our team is ready to assist.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.link || '#'}
                className={`group relative bg-white dark:bg-gray-900 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-800 animate-fade-in-up hover:-translate-y-3`}
                style={{ animationDelay: method.delay }}
              >
                <div className="relative z-10">
                  <div className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${method.gradient} rounded-2xl flex items-center justify-center text-white mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {method.icon}
                  </div>
                  
                  <h3 className={`text-xl md:text-2xl font-bold mb-3 ${method.color} group-hover:scale-105 transition-transform`}>
                    {method.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base mb-3 break-words leading-relaxed">
                    {method.value}
                  </p>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-500 font-medium">
                    {method.description}
                  </p>
                </div>
                
                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex p-4 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl mb-4 shadow-lg">
              <MessageSquare className="w-8 h-8 md:w-10 md:h-10 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Send Us a <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Message</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Fill out the form below and we'll get back to you within 1 business hour
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className={`bg-white dark:bg-gray-900 rounded-3xl p-6 md:p-8 lg:p-12 shadow-2xl border border-gray-200 dark:border-gray-800 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {submitted && (
              <div className="mb-6 p-4 md:p-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl flex items-center gap-3 animate-fade-in">
                <CheckCircle className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Message sent successfully!</p>
                  <p className="text-sm opacity-90">We'll contact you within 1 business hour.</p>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all ${
                    formErrors.name 
                      ? 'border-red-500 focus:border-red-500' 
                      : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                  }`}
                  placeholder="John Doe"
                  aria-label="Full name"
                />
                {formErrors.name && (
                  <p className="text-sm text-red-600 dark:text-red-400">{formErrors.name}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all ${
                    formErrors.email 
                      ? 'border-red-500 focus:border-red-500' 
                      : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                  }`}
                  placeholder="john@example.com"
                  aria-label="Email address"
                />
                {formErrors.email && (
                  <p className="text-sm text-red-600 dark:text-red-400">{formErrors.email}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all focus:border-blue-500"
                  placeholder="+91 98765 43210"
                  aria-label="Phone number"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all focus:border-blue-500"
                  placeholder="Project Inquiry"
                  aria-label="Subject"
                />
              </div>
            </div>

            <div className="mb-8 space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Message *
              </label>
              <textarea
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                rows="6"
                className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white resize-none transition-all ${
                  formErrors.message 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                }`}
                placeholder="Tell us about your project, requirements, timeline, and any specific challenges you're facing..."
                aria-label="Message"
              ></textarea>
              {formErrors.message && (
                <p className="text-sm text-red-600 dark:text-red-400">{formErrors.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group relative w-full px-8 py-4 md:py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
              aria-label="Send message"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
              
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white relative z-10"></div>
                  <span className="relative z-10">Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 md:w-6 md:h-6 relative z-10 group-hover:rotate-12 transition-transform" />
                  <span className="text-lg relative z-10">Send Message</span>
                </>
              )}
            </button>

            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>Your information is secure and private</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Response time: 1 business hour</span>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-16 md:py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
            <Globe className="w-4 h-4 mr-2 text-white" />
            <span className="text-sm font-medium text-white">Stay Connected</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Follow Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Journey</span>
          </h2>
          
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
            Connect with us on social media for updates, insights, and tech trends
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`group relative w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br ${social.gradient} text-white rounded-2xl flex items-center justify-center transition-all duration-500 transform hover:scale-110 hover:shadow-2xl animate-fade-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
                aria-label={social.label}
              >
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 rounded-2xl transition-colors"></div>
                <div className="relative z-10 transform group-hover:scale-110 transition-transform">
                  {social.icon}
                </div>
                <div className="absolute -bottom-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-xs bg-gray-900 text-white px-2 py-1 rounded whitespace-nowrap">
                    {social.label}
                  </span>
                </div>
              </a>
            ))}
          </div>
          
          <div className="mt-12 p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl max-w-md mx-auto">
            <div className="flex items-center justify-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                Schedule a consultation
              </span>
              <a 
                href={`mailto:${COMPANY_INFO.contact.email}?subject=Schedule%20Consultation`}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold"
              >
                Book Now →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          33% {
            transform: translateY(-20px) rotate(5deg);
          }
          66% {
            transform: translateY(10px) rotate(-5deg);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          opacity: 0;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        /* Mobile Optimization */
        @media (max-width: 768px) {
          .text-7xl {
            font-size: 3.5rem;
          }
          
          .text-6xl {
            font-size: 3rem;
          }
          
          .text-5xl {
            font-size: 2.5rem;
          }
          
          .text-4xl {
            font-size: 2rem;
          }
          
          .text-3xl {
            font-size: 1.75rem;
          }
        }

        /* Reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in-up,
          .animate-fade-in,
          .animate-float,
          .transform,
          .transition-all,
          .transition-transform,
          .transition-opacity {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}